import React, { useState, useEffect, useRef } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useTheme } from '../../context/ThemeContext';
import {
  Search,
  Plus,
  Compass,
  CheckSquare,
  Layers,
  ArrowRight,
  User,
  Sun,
  Moon,
  FolderKanban,
  Settings,
  Sparkles,
  Inbox
} from 'lucide-react';

export const CommandPalette = ({ isOpen, onClose, setCurrentView, setCurrentProjectView }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const {
    projects = [],
    allTasks = [],
    members = [],
    setActiveProjectId,
    setIsQuickAddOpen,
    setIsCreateProjectModalOpen,
    setIsInviteModalOpen,
    setSelectedTask
  } = useWorkspace();

  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const q = query.trim().toLowerCase();

  // 1. Actions & Commands
  const staticActions = [
    {
      id: 'act_new_task',
      category: 'Actions',
      title: 'Create new task',
      subtitle: 'Shortcut: Q • Add a task to the active project',
      icon: <Plus size={16} color="var(--accent-teal)" />,
      action: () => {
        onClose();
        setIsQuickAddOpen(true);
      }
    },
    {
      id: 'act_new_proj',
      category: 'Actions',
      title: 'Create new project',
      subtitle: 'Initialize a new modular project workspace',
      icon: <FolderKanban size={16} color="var(--accent-teal)" />,
      action: () => {
        onClose();
        setIsCreateProjectModalOpen(true);
      }
    },
    {
      id: 'act_invite',
      category: 'Actions',
      title: 'Invite member to workspace',
      subtitle: 'Add collaborator with custom role permissions',
      icon: <User size={16} color="var(--color-info)" />,
      action: () => {
        onClose();
        setIsInviteModalOpen(true);
      }
    },
    {
      id: 'act_theme',
      category: 'Actions',
      title: `Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`,
      subtitle: `Currently using ${theme} neomorphic dual-shadow palette`,
      icon: theme === 'dark' ? <Sun size={16} color="var(--color-warning)" /> : <Moon size={16} color="var(--color-purple)" />,
      action: () => {
        toggleTheme();
        onClose();
      }
    },
    {
      id: 'act_inbox',
      category: 'Navigation',
      title: 'Go to Inbox',
      subtitle: 'Actionable updates, assignments & mentions',
      icon: <Inbox size={16} color="var(--color-info)" />,
      action: () => {
        setCurrentView('inbox');
        onClose();
      }
    },
    {
      id: 'act_modules',
      category: 'Navigation',
      title: 'Open Module Library',
      subtitle: 'Browse and toggle workspace capabilities',
      icon: <Compass size={16} color="var(--accent-teal)" />,
      action: () => {
        setCurrentView('module_library');
        onClose();
      }
    },
    {
      id: 'act_settings',
      category: 'Navigation',
      title: 'Workspace Settings',
      subtitle: 'Manage members, billing & integrations',
      icon: <Settings size={16} color="var(--text-muted)" />,
      action: () => {
        setCurrentView('settings');
        onClose();
      }
    }
  ];

  const matchingActions = staticActions.filter(
    (a) => a.title.toLowerCase().includes(q) || a.subtitle.toLowerCase().includes(q)
  );

  // 2. Projects
  const matchingProjects = projects
    .filter((p) => p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q))
    .map((p) => ({
      id: `prj_${p.id}`,
      category: 'Projects',
      title: p.name,
      subtitle: `${p.key} • ${p.category} • Lead: ${p.lead}`,
      icon: <Layers size={16} color="var(--accent-teal)" />,
      action: () => {
        setActiveProjectId(p.id);
        setCurrentView('project_view');
        if (setCurrentProjectView) setCurrentProjectView('board');
        onClose();
      }
    }));

  // 3. Tasks
  const matchingTasks = allTasks
    .filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.labels || []).some((l) => l.toLowerCase().includes(q))
    )
    .slice(0, 6)
    .map((t) => ({
      id: `task_${t.id}`,
      category: 'Tasks',
      title: t.title,
      subtitle: `${t.id} • ${t.status.replace('_', ' ').toUpperCase()} • ${t.priority.toUpperCase()}`,
      icon: <CheckSquare size={16} color="var(--color-info)" />,
      action: () => {
        setSelectedTask(t);
        onClose();
      }
    }));

  // 4. Members
  const matchingMembers = members
    .filter((m) => m.user.name.toLowerCase().includes(q) || m.user.email.toLowerCase().includes(q) || m.role.toLowerCase().includes(q))
    .map((m) => ({
      id: `mem_${m.membershipId}`,
      category: 'Members',
      title: m.user.name,
      subtitle: `${m.role} • ${m.user.email}`,
      avatar: m.user.avatar,
      icon: <User size={16} color="var(--color-purple)" />,
      action: () => {
        setCurrentView('members');
        onClose();
      }
    }));

  const allResults = [...matchingActions, ...matchingProjects, ...matchingTasks, ...matchingMembers];

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (allResults.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + allResults.length) % (allResults.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allResults[selectedIndex]) {
        allResults[selectedIndex].action();
      }
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '10vh',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(8px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '620px',
          backgroundColor: 'var(--surface-card)',
          boxShadow: 'var(--shadow-raised-hover)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-container)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        className="animate-modal"
      >
        {/* Search Input Bar */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-divider)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <Search size={20} style={{ color: 'var(--accent-teal)' }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command, project, task ID, or teammate name..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-body)'
            }}
          />
          <kbd
            style={{
              padding: '3px 7px',
              fontSize: '11px',
              backgroundColor: 'var(--surface-recessed)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '4px',
              color: 'var(--text-muted)',
              fontWeight: '700'
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results Stream */}
        <div
          style={{
            maxHeight: '380px',
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {allResults.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              No matching commands, projects, or tasks found for "{query}"
            </div>
          ) : (
            allResults.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <div
                  key={item.id}
                  onClick={item.action}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: isSelected ? 'var(--surface-recessed)' : 'transparent',
                    boxShadow: isSelected ? 'var(--shadow-recessed-sm)' : 'none',
                    borderLeft: isSelected ? '3px solid var(--accent-teal)' : '3px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.1s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: 'var(--surface-card)',
                        boxShadow: 'var(--shadow-raised-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {item.avatar ? (
                        <img
                          src={item.avatar}
                          alt={item.title}
                          style={{ width: '28px', height: '28px', borderRadius: '999px', objectFit: 'cover' }}
                        />
                      ) : (
                        item.icon
                      )}
                    </div>
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {item.subtitle}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: '700',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--surface-card)',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--border-subtle)',
                        textTransform: 'uppercase'
                      }}
                    >
                      {item.category}
                    </span>
                    <ArrowRight
                      size={14}
                      style={{
                        color: isSelected ? 'var(--accent-teal)' : 'transparent',
                        transform: isSelected ? 'translateX(2px)' : 'none',
                        transition: 'all 0.15s'
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div
          style={{
            padding: '10px 18px',
            borderTop: '1px solid var(--border-divider)',
            backgroundColor: 'var(--surface-alt)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: 'var(--text-muted)'
          }}
        >
          <span>Use ↑↓ to navigate, Enter to select</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Sparkles size={12} color="var(--accent-teal)" /> Forma Universal Command
          </span>
        </div>
      </div>
    </div>
  );
};
